import { Box, TableRow, TableHead, TableContainer, TableCell, TableBody, Typography, Dialog, Rating, Button, TextField, Stack } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { StarBorder } from "@mui/icons-material";
import { Star } from "@mui/icons-material";
import { CompanyLogo } from "../../../company/components/CompanyLogo";
import { DoNotDisturb } from "@mui/icons-material";
import { DisplayBoxBase } from "../../../../components/shared/StyledComponents";
import { FollowUpPrompts } from "./FollowupPrompts";
import { RATING_LABELS } from "../../../../constants/ratings";

export const RatingDialog = ({ meeting, advisor, onRate, onClose, suggestedRating }: { meeting: any, advisor: any, onRate: (meetingUuid: string, rating: number | null, comment: string | null) => void, onClose: () => void, suggestedRating: number | null }) => {
    const [rating, setRating] = useState<number | null>(meeting.ratings?.[0]?.rating || null);
    const [hoveredRating, setHoveredRating] = useState<number | null>(null);
    const [comment, setComment] = useState<string | null>(meeting.ratings?.[0]?.comment || "");
    const hasHandledInitialRating = useRef(false);
    const [showFollowup, setShowFollowup] = useState(false);

    useEffect(() => {
        if (suggestedRating !== null && !rating && !hasHandledInitialRating.current) {
            hasHandledInitialRating.current = true;
            handleRatingChange(null, suggestedRating);
        }
    }, [suggestedRating]);

    const handleRatingChange = (_event: any, newValue: number | null) => {
        setRating(newValue);
        if (newValue !== null) {
            onRate(meeting.uuid, newValue, comment);
        }
    };

    const handleSubmitFeedback = () => {
        onRate(meeting.uuid, rating, comment);
        if (rating !== null && rating >= 3) {
            setShowFollowup(true);
        } else {
            onClose();
        }
    }

    const handleClose = () => {
        if (rating !== null && rating >= 3 && !showFollowup) {
            setShowFollowup(true);
        } else {
            onClose();
        }
    }

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: showFollowup ? '750px' : '550px',
                    maxWidth: showFollowup ? '750px' : '550px'
                }
            }}
        >
            <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {showFollowup ?
                        <Box>
                            <h5>Three ways you can help {advisor.survey_link.project.name}</h5>
                            <Typography variant="body1" mb={2}>Glad you had a great meeting with {advisor.survey_link.project.name}! Here are additional ways you can help.</Typography>
                            <FollowUpPrompts advisor={advisor} />
                        </Box> : (
                            <Box>

                                <h5>Please rate your meeting with {advisor.survey_link.project.name}</h5>
                                <p>We hope you had a great meeting with {advisor.survey_link.project.name}. Please rate this meeting so that we can steer more of the right introductions your way.</p>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', backgroundColor: "#ffffff", borderRadius: "20px", padding: "20px", border: "1px solid #c9c9c9" }}>
                                    <CompanyLogo project={advisor.survey_link.project} size="large" />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: "40px", width: '100%' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <DoNotDisturb
                                                    onClick={(e) => {
                                                        handleRatingChange(e, 0);
                                                    }}
                                                    onMouseOver={() => setHoveredRating(0)}
                                                    onMouseLeave={() => setHoveredRating(-1)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        width: '50px',
                                                        height: '50px',
                                                        paddingBottom: '5px',
                                                        color: rating === 0 ? 'error.main' : 'action.disabled',
                                                        '&:hover': { color: 'error.main' }
                                                    }}
                                                />
                                                <Rating
                                                    value={rating}
                                                    max={4}
                                                    size="large"
                                                    icon={<Star sx={{ width: '50px', height: '50px' }} />}
                                                    emptyIcon={<StarBorder sx={{ width: '50px', height: '50px' }} />}
                                                    onChange={handleRatingChange}
                                                    onChangeActive={(_event, newHover) => {
                                                        setHoveredRating(newHover);
                                                    }}
                                                />
                                            </Box>
                                            <Typography>
                                                {hoveredRating !== null && hoveredRating !== -1
                                                    ? RATING_LABELS[hoveredRating]
                                                    : rating !== null
                                                        ? RATING_LABELS[rating]
                                                        : "Please rate the meeting"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {rating !== null && (
                                    <Stack mt={2} gap={2} direction="column" alignItems="flex-end">
                                        <Box sx={{ width: '100%' }}>
                                            {rating < 4 ? (
                                                <Typography variant="body1" fontWeight="bold" mb={1}>What kept this meeting from being great?</Typography>
                                            ) : (
                                                <Typography variant="body1" fontWeight="bold" mb={1}>What made this meeting great?</Typography>
                                            )}
                                            <TextField
                                                label="Write 1 or 2 sentences that will help us improve these moving forward."
                                                multiline
                                                rows={2}
                                                value={comment}
                                                onChange={(e) => setComment(e.target.value)}
                                                sx={{ width: '100%' }}
                                            />
                                        </Box>
                                        <Button variant="contained" onClick={handleSubmitFeedback}>Submit Feedback</Button>
                                    </Stack>
                                )}
                            </Box>)}
                </Box>
            </Box>
        </Dialog >
    );
};
